import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  TrendingUp,
  GripHorizontal,
  TrendingDown,
  Filter,
  Calendar,
  Target,
  BarChart3,
  Users,
  DollarSign,
  Settings,
  Search,
  FileDown,
  Gauge,
  Presentation,
} from "lucide-react";
import { authenticatedFetch } from "../services/auth";
import { API_BASE } from "../utils/apiConfig";
import PDFExportModal from "./PDFExportModal";
import { generateScorecardPPTX } from "./pptxExport";

const BalancedScorecard = ({ onClose }) => {
  const [scorecardData, setScorecardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [availablePeriodsData, setAvailablePeriodsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [isGeneratingPptx, setIsGeneratingPptx] = useState(false);
  const [globalPerf, setGlobalPerf] = useState(null);

  useEffect(() => {
    fetchAvailablePeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchScorecardData();
    }
  }, [selectedPeriod]);

  const fetchAvailablePeriods = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/bsc/periods`);

      if (response.ok) {
        const periodsData = await response.json();
        console.log("✅ Períodos carregados da API:", periodsData);

        // Ordenar por data (mais recente primeiro)
        const sortedPeriodsData = periodsData.sort(
          (a, b) => new Date(b.endDate) - new Date(a.endDate),
        );

        // Armazenar dados completos dos períodos
        setAvailablePeriodsData(sortedPeriodsData);

        // Extrair apenas os códigos dos períodos
        const periodCodes = sortedPeriodsData.map((p) => p.periodCode);
        setAvailablePeriods(periodCodes);

        // Selecionar automaticamente o último período (mais recente)
        if (periodCodes.length > 0) {
          setSelectedPeriod(periodCodes[0]);
          console.log("✅ Período mais recente selecionado:", periodCodes[0]);
          console.log(
            "✅ END_DATE do período selecionado:",
            sortedPeriodsData[0].endDate,
          );
        }
      } else {
        console.warn(
          "⚠️ Falha ao buscar períodos da API, usando períodos mock",
        );
        // Fallback para períodos mock
        const mockPeriodsData = [
          { periodCode: "jun/25", endDate: "2025-06-30T00:00:00Z" },
          { periodCode: "mai/25", endDate: "2025-05-31T00:00:00Z" },
          { periodCode: "abr/25", endDate: "2025-04-30T00:00:00Z" },
          { periodCode: "mar/25", endDate: "2025-03-31T00:00:00Z" },
        ];
        const mockPeriods = mockPeriodsData.map((p) => p.periodCode);
        setAvailablePeriodsData(mockPeriodsData);
        setAvailablePeriods(mockPeriods);
        setSelectedPeriod(mockPeriods[0]);
      }
    } catch (error) {
      console.warn(
        "⚠️ Erro ao buscar períodos:",
        error,
        "- usando períodos mock",
      );
      // Fallback para períodos mock
      const mockPeriodsData = [
        { periodCode: "jun/25", endDate: "2025-06-30T00:00:00Z" },
        { periodCode: "mai/25", endDate: "2025-05-31T00:00:00Z" },
        { periodCode: "abr/25", endDate: "2025-04-30T00:00:00Z" },
        { periodCode: "mar/25", endDate: "2025-03-31T00:00:00Z" },
      ];
      const mockPeriods = mockPeriodsData.map((p) => p.periodCode);
      setAvailablePeriodsData(mockPeriodsData);
      setAvailablePeriods(mockPeriods);
      setSelectedPeriod(mockPeriods[0]);
    }
  };

  const fetchScorecardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Converter período selecionado para datas de início e fim
      const { startDate, endDate } = getPeriodDateRange(selectedPeriod);

      const url = `${API_BASE}/api/bsc/scorecard?start_date=${startDate}&end_date=${endDate}`;
      console.log(
        `🔄 Buscando dados do scorecard para período ${selectedPeriod}: ${url}`,
      );

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setScorecardData(Array.isArray(data) ? data : []);
      console.log(
        `✅ Dados do scorecard carregados da API para período ${selectedPeriod}:`,
        data,
      );

      // Performance global ponderada do período (card do topo)
      try {
        const perfResp = await authenticatedFetch(
          `${API_BASE}/api/bsc/performance?start_date=${startDate}&end_date=${endDate}`,
        );
        if (perfResp.ok) {
          setGlobalPerf(await perfResp.json());
        } else {
          setGlobalPerf(null);
        }
      } catch (e) {
        console.warn("Falha ao carregar performance global:", e);
        setGlobalPerf(null);
      }
    } catch (apiError) {
      console.warn(
        `⚠️ Falha na API do scorecard para período ${selectedPeriod}, usando dados mock:`,
        apiError,
      );

      // Calcular períodos históricos para os dados mock
      const mockHistoricalPeriods = getHistoricalPeriods();

      // Função para calcular a média de performance dos indicadores de um objetivo
      const calculateObjectiveAverage = (indicators) => {
        if (!indicators || indicators.length === 0) return 0;
        const validPercents = indicators
          .filter((ind) => ind.current && ind.current.percent !== undefined)
          .map((ind) => ind.current.percent);

        if (validPercents.length === 0) return 0;

        return (
          validPercents.reduce((sum, percent) => sum + percent, 0) /
          validPercents.length
        );
      };

      // Mock data based on attached format
      const mockData = [
        {
          perspective: "FINANCEIRO",
          currentPeriodLabel: selectedPeriod,
          historyLabels: mockHistoricalPeriods.map((p) => ({ label: p })),
          objectives: [
            {
              objectiveId: 1,
              objective: "Maximizar o Retorno do Capital Investido",
              indicators: [
                {
                  indicatorId: 101,
                  indicator: "ROIC",
                  unit: "%",
                  history: [
                    { periodLabel: mockHistoricalPeriods[0], percent: 0.12 },
                    { periodLabel: mockHistoricalPeriods[1], percent: 0.33 },
                    { periodLabel: mockHistoricalPeriods[2], percent: -0.04 },
                  ],
                  current: {
                    realized: 0.84,
                    target: 0.9,
                    percent: 93.3,
                    statusT: "N",
                    trend: "up",
                  },
                },
              ],
            },
            {
              objectiveId: 2,
              objective: "Aumentar Receita",
              indicators: [
                {
                  indicatorId: 102,
                  indicator: "RECEITA LÍQUIDA TOTAL",
                  unit: "R$",
                  history: [
                    {
                      periodLabel: mockHistoricalPeriods[0],
                      percent: 4746443.85,
                    },
                    {
                      periodLabel: mockHistoricalPeriods[1],
                      percent: 4394191.9,
                    },
                    {
                      periodLabel: mockHistoricalPeriods[2],
                      percent: 2992922.84,
                    },
                  ],
                  current: {
                    realized: 4103902.73,
                    target: 5000000,
                    percent: 82.1,
                    statusT: "N",
                    trend: "up",
                  },
                },
              ],
            },
          ],
        },
        {
          perspective: "CLIENTES",
          currentPeriodLabel: selectedPeriod,
          historyLabels: mockHistoricalPeriods.map((p) => ({ label: p })),
          objectives: [
            {
              objectiveId: 3,
              objective: "Aumentar a Satisfação dos Clientes",
              indicators: [
                {
                  indicatorId: 301,
                  indicator: "NPS (Net Promoter Score)",
                  unit: "pontos",
                  history: [
                    { periodLabel: mockHistoricalPeriods[0], percent: 75 },
                    { periodLabel: mockHistoricalPeriods[1], percent: 78 },
                    { periodLabel: mockHistoricalPeriods[2], percent: 80 },
                  ],
                  current: {
                    realized: 82,
                    target: 85,
                    percent: 96.5,
                    statusT: "N",
                    trend: "up",
                  },
                },
              ],
            },
          ],
        },
        {
          perspective: "PROCESSOS",
          currentPeriodLabel: selectedPeriod,
          historyLabels: mockHistoricalPeriods.map((p) => ({ label: p })),
          objectives: [
            {
              objectiveId: 4,
              objective: "Reduzir o Lead Time",
              indicators: [
                {
                  indicatorId: 401,
                  indicator: "Tempo Médio de Produção",
                  unit: "dias",
                  history: [
                    { periodLabel: mockHistoricalPeriods[0], percent: 70 },
                    { periodLabel: mockHistoricalPeriods[1], percent: 65 },
                    { periodLabel: mockHistoricalPeriods[2], percent: 60 },
                  ],
                  current: {
                    realized: 55,
                    target: 60,
                    percent: 91.6,
                    statusT: "T",
                    trend: "down",
                  },
                },
              ],
            },
          ],
        },
        {
          perspective: "PESSOAS",
          currentPeriodLabel: selectedPeriod,
          historyLabels: mockHistoricalPeriods.map((p) => ({ label: p })),
          objectives: [
            {
              objectiveId: 5,
              objective: "Desenvolver Competências Estratégicas",
              indicators: [
                {
                  indicatorId: 501,
                  indicator: "Horas de Treinamento por Colaborador",
                  unit: "h",
                  history: [
                    { periodLabel: mockHistoricalPeriods[0], percent: 80 },
                    { periodLabel: mockHistoricalPeriods[1], percent: 85 },
                    { periodLabel: mockHistoricalPeriods[2], percent: 90 },
                  ],
                  current: {
                    realized: 95,
                    target: 100,
                    percent: 95,
                    statusT: "N",
                    trend: "up",
                  },
                },
              ],
            },
          ],
        },
      ];

      // Calcular a média para cada objetivo
      mockData.forEach((perspective) => {
        perspective.objectives.forEach((objective) => {
          objective.average = calculateObjectiveAverage(objective.indicators);
        });
      });

      setScorecardData(Array.isArray(mockData) ? mockData : []);
      console.log("✅ Dados mock do scorecard carregados");
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const getPerspectiveIcon = (perspective) => {
    switch (perspective.toUpperCase()) {
      case "FINANCEIRO":
        return <DollarSign className="w-4 h-4" />;
      case "CLIENTES":
        return <Users className="w-4 h-4" />;
      case "PROCESSOS":
        return <Settings className="w-4 h-4" />;
      case "PESSOAS":
        return <Target className="w-4 h-4" />;
      default:
        return <BarChart3 className="w-4 h-4" />;
    }
  };

  const getPerspectiveColor = (perspective) => {
    switch (perspective.toUpperCase()) {
      case "FINANCEIRO":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "CLIENTES":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "PROCESSOS":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "PESSOAS":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === "upV") {
      return <TrendingUp className="w-4 h-4 text-green-600" />;
    } else if (trend === "upR") {
      return <TrendingUp className="w-4 h-4 text-red-600" />;
    } else if (trend === "downR") {
      return <TrendingDown className="w-4 h-4 text-red-600" />;
    } else if (trend === "downV") {
      return <TrendingDown className="w-4 h-4 text-green-600" />;
    } else if (trend === "w") {
      // ícone neutro (pode ser um traço ou nenhum ícone)
      return <GripHorizontal className="w-4 h-4 text-blue-600" />;
    } else {
      // caso não exista tendência, retorna vazio
      return null;
    }
  };

  const getStatusIcon = (statusT) => {
    return statusT === "T" ? (
      <span className="text-green-600 font-bold">✓</span>
    ) : (
      <span className="text-yellow-600 font-bold">!</span>
    );
  };

  const formatValue = (value, unit) => {
    if (typeof value === "number") {
      if (unit === "R$") {
        return `R$ ${value.toLocaleString("pt-BR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      }

      if (unit === "%") {
        return `${value.toFixed(2)}%`;
      }

      return `${value.toFixed(2)} ${unit}`;
    }

    return `${value} ${unit}`;
  };

  const getHistoryValue = (indicator, periodLabel) => {
    const historyItem = indicator.history?.find(
      (h) => h.periodLabel === periodLabel,
    );
    return historyItem ? historyItem.percent : "N/D";
  };

  // Função para converter período selecionado em range de datas usando END_DATE
  const getPeriodDateRange = (period) => {
    if (!period) {
      return {
        startDate: "2025-01-01",
        endDate: "2025-12-31",
      };
    }

    // Procurar o período nos dados carregados da API
    const periodData = availablePeriodsData?.find(
      (p) => p.periodCode === period,
    );

    if (periodData && periodData.endDate) {
      // Usar o END_DATE do banco como referência
      const endDate = new Date(periodData.endDate);

      // Calcular o primeiro dia do mesmo mês
      const startDate = new Date(
        endDate.getFullYear(),
        endDate.getMonth() - 3,
        1,
      );

      return {
        startDate: startDate.toISOString().split("T")[0],
        endDate: endDate.toISOString().split("T")[0],
      };
    }

    // Fallback: tentar parsear diferentes formatos
    console.warn(`⚠️ Período ${period} não encontrado na API, usando fallback`);

    const monthMap = {
      jan: 1,
      fev: 2,
      mar: 3,
      abr: 4,
      mai: 5,
      jun: 6,
      jul: 7,
      ago: 8,
      set: 9,
      out: 10,
      nov: 11,
      dez: 12,
    };

    let month, year;

    // Formato "jan-2025", "fev-2025", etc.
    if (period.includes("-") && period.match(/^[a-z]{3}-\d{4}$/i)) {
      const [monthStr, yearStr] = period.split("-");
      month = monthMap[monthStr.toLowerCase()];
      year = parseInt(yearStr);
    }
    // Formato "jun/25", "jul/25", etc.
    else if (period.includes("/")) {
      const [monthStr, yearStr] = period.split("/");
      month = monthMap[monthStr.toLowerCase()];
      year = parseInt(`20${yearStr}`);
    }

    if (month && year) {
      const startDate = new Date(year, month - 3, 1);
      const endDate = new Date(year, month, 0);

      return {
        startDate: startDate.toISOString().split("T")[0],
        endDate: endDate.toISOString().split("T")[0],
      };
    }

    return {
      startDate: "2025-01-01",
      endDate: "2025-12-31",
    };
  };

  // Função para calcular os 3 períodos anteriores
  const getHistoricalPeriods = () => {
    if (!selectedPeriod) return ["", "", ""];

    const monthMap = {
      jan: 1,
      fev: 2,
      mar: 3,
      abr: 4,
      mai: 5,
      jun: 6,
      jul: 7,
      ago: 8,
      set: 9,
      out: 10,
      nov: 11,
      dez: 12,
    };

    const reverseMonthMap = {
      1: "jan",
      2: "fev",
      3: "mar",
      4: "abr",
      5: "mai",
      6: "jun",
      7: "jul",
      8: "ago",
      9: "set",
      10: "out",
      11: "nov",
      12: "dez",
    };

    let month, year;

    // Formato "jan-2025", "fev-2025", etc.
    if (
      selectedPeriod.includes("-") &&
      selectedPeriod.match(/^[a-z]{3}-\d{4}$/i)
    ) {
      const [monthStr, yearStr] = selectedPeriod.split("-");
      month = monthMap[monthStr.toLowerCase()];
      year = parseInt(yearStr);
    }
    // Formato "jun/25", "jul/25", etc.
    else if (selectedPeriod.includes("/")) {
      const [monthStr, yearStr] = selectedPeriod.split("/");
      month = monthMap[monthStr.toLowerCase()];
      year = parseInt(`20${yearStr}`);
    }

    if (!month || !year) return ["", "", ""];

    const periods = [];
    for (let i = 3; i >= 1; i--) {
      let calcMonth = month - i;
      let calcYear = year;

      if (calcMonth <= 0) {
        calcMonth += 12;
        calcYear -= 1;
      }

      const monthStr = reverseMonthMap[calcMonth];
      const yearStr = selectedPeriod.includes("/")
        ? calcYear.toString().slice(-2)
        : calcYear.toString();

      const separator = selectedPeriod.includes("/") ? "/" : "-";
      periods.push(`${monthStr}${separator}${yearStr}`);
    }

    return periods;
  };

  const perfColors = (perc) => {
    if (perc >= 100) return "bg-green-100 text-green-800";
    if (perc >= 80) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  const handlePDFExport = () => {
    if (!filteredData || filteredData.length === 0) {
      alert("Nenhum dado disponível para exportação. Certifique-se de que há dados carregados no scorecard.");
      return;
    }
    setShowPDFModal(true);
  };

  const handlePptxExport = async () => {
    if (!filteredData || filteredData.length === 0) {
      alert("Nenhum dado disponível para exportação. Certifique-se de que há dados carregados no scorecard.");
      return;
    }
    try {
      setIsGeneratingPptx(true);
      await generateScorecardPPTX({ scorecardData: filteredData, selectedPeriod, globalPerf });
    } catch (e) {
      console.error("Erro ao gerar PPTX:", e);
      alert("❌ Erro ao gerar o PPTX. Verifique o console para mais detalhes.");
    } finally {
      setIsGeneratingPptx(false);
    }
  };

  const historicalPeriods = getHistoricalPeriods();

  const filteredData = (scorecardData || []).filter((perspective) => {
    const matchesPerspective = perspective.perspective
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesObjectivesOrIndicators = perspective.objectives?.some(
      (objective) =>
        objective.objective?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        objective.indicators?.some((indicator) =>
          indicator.indicator?.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
    );

    return matchesPerspective || matchesObjectivesOrIndicators;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
          <p className="text-gray-600 font-medium">
            Carregando Balanced Scorecard...
          </p>
        </div>
      </div>
    );
  }

  // Cores por faixa de performance
  const perfText = (v) =>
    v >= 100
      ? "text-green-600"
      : v >= 80
        ? "text-emerald-600"
        : v >= 50
          ? "text-amber-600"
          : "text-red-600";
  const perfBg = (v) =>
    v >= 100
      ? "bg-green-500"
      : v >= 80
        ? "bg-emerald-500"
        : v >= 50
          ? "bg-amber-500"
          : "bg-red-500";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header do Mapa Estratégico */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Balanced Scorecard
                  </h1>
                  <p className="text-gray-600 text-sm">
                    Painel de controle estratégico integrado
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card de Performance Global do período */}
      {globalPerf && typeof globalPerf.performance === "number" && (
        <div className="px-4 lg:px-8 pt-6">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl shadow-lg ${perfBg(globalPerf.performance)}`}>
                  <Gauge className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    Performance Global{globalPerf.periodLabel ? ` — ${globalPerf.periodLabel}` : ""}
                  </p>
                  <div className="flex items-end gap-3">
                    <span className={`text-4xl font-bold ${perfText(globalPerf.performance)}`}>
                      {globalPerf.performance.toFixed(1)}%
                    </span>
                    {globalPerf.previous != null && (
                      <span
                        className={`text-sm font-medium flex items-center gap-1 ${
                          globalPerf.trend === "up"
                            ? "text-green-600"
                            : globalPerf.trend === "down"
                              ? "text-red-600"
                              : "text-gray-500"
                        }`}
                      >
                        {globalPerf.trend === "up" ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : globalPerf.trend === "down" ? (
                          <TrendingDown className="w-4 h-4" />
                        ) : null}
                        {(globalPerf.performance - globalPerf.previous).toFixed(1)} p.p.
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(globalPerf.perspectives || []).map((p) => (
                  <div
                    key={p.perspective}
                    className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 min-w-[110px]"
                  >
                    <p className="text-xs text-gray-500 truncate">{p.perspective}</p>
                    <p className={`text-sm font-semibold ${perfText(p.performance)}`}>
                      {p.performance.toFixed(1)}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="px-4 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200">
          {/* Filtros */}
          <div className="p-5 border-b border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filtros e Busca
              </h3>

              <div className="flex items-center gap-3">
                <button
                  onClick={handlePDFExport}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <FileDown className="w-4 h-4" />
                  Exportar PDF
                </button>
                <button
                  onClick={handlePptxExport}
                  disabled={isGeneratingPptx}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#7a2433] to-[#5e1a26] hover:from-[#8a2a3b] hover:to-[#6e1f2d] text-white rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isGeneratingPptx ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Gerando PPTX...
                    </>
                  ) : (
                    <>
                      <Presentation className="w-4 h-4" />
                      Exportar PPTX
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Busca */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buscar por Perspectiva, Objetivo ou Indicador
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Busque por perspectiva, objetivo ou indicador..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Filtro de Período */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Período
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                  >
                    {availablePeriods
                      .map((period) => (
                        <option key={period} value={period}>
                          {period}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Scorecard Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th
                    className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider"
                    rowSpan="2"
                  >
                    Objetivo Estratégico
                  </th>
                  <th
                    className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider"
                    rowSpan="2"
                  >
                    Status
                  </th>
                  <th
                    className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider"
                    rowSpan="2"
                  >
                    Item de Controle
                  </th>
                  <th
                    className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider bg-gray-500"
                    colSpan="3"
                  >
                    Histórico
                  </th>
                  <th
                    className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider bg-green-500"
                    colSpan="3"
                  >
                    {selectedPeriod}
                  </th>
                  <th
                    className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider"
                    rowSpan="2"
                  >
                    Status T
                  </th>
                  <th
                    className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider"
                    rowSpan="2"
                  >
                    Trend
                  </th>
                </tr>
                <tr>
                  <th className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider bg-gray-400">
                    {historicalPeriods[0] || "H-3"}
                  </th>
                  <th className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider bg-gray-400">
                    {historicalPeriods[1] || "H-2"}
                  </th>
                  <th className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider bg-gray-400">
                    {historicalPeriods[2] || "H-1"}
                  </th>
                  <th className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider bg-green-400">
                    Realizado
                  </th>
                  <th className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider bg-green-400">
                    Meta
                  </th>
                  <th className="px-4 py-2 text-center text-sm font-bold uppercase tracking-wider bg-green-400">
                    %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((perspectiveData) => (
                  <React.Fragment key={perspectiveData.perspective}>
                    {/* Perspective Header Row */}
                    <tr
                      className={`${getPerspectiveColor(perspectiveData.perspective)} border-l-4`}
                    >
                      <td
                        colSpan="11"
                        className="px-4 py-3 font-bold text-sm uppercase tracking-wide"
                      >
                        <div className="flex items-center gap-2">
                          {getPerspectiveIcon(perspectiveData.perspective)}
                          {perspectiveData.perspective}
                        </div>
                      </td>
                    </tr>

                    {/* Objectives and Indicators */}
                    {perspectiveData.objectives.map((objective, objIndex) =>
                      objective.indicators.map((indicator, indIndex) => (
                        <tr
                          key={`${objective.objectiveId}-${indicator.indicatorId}`}
                          className="hover:bg-gray-50"
                        >
                          {/* Objective Strategic (only show on first indicator of objective) */}
                          {indIndex === 0 && (
                            <td
                              rowSpan={objective.indicators.length}
                              className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 bg-gray-50"
                            >
                              <div className="max-w-xs">
                                {objective.objective}
                              </div>
                            </td>
                          )}

                          {/* Status */}
                          {indIndex === 0 && (
                            <td
                              rowSpan={objective.indicators.length}
                              className="px-4 py-4 text-center"
                            >
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${perfColors(objective.average)}`}
                              >
                                {objective.average.toFixed(2)}%
                              </span>
                            </td>
                          )}

                          {/* Item de Controle */}
                          <td className="px-4 py-4 text-sm text-gray-900">
                            <div className="max-w-xs">
                              {indicator.indicator}
                            </div>
                          </td>

                          {/* Historical data columns */}
                          <td className="px-4 py-4 text-center text-sm bg-gray-50">
                            {getHistoryValue(
                              indicator,
                              historicalPeriods[0],
                            ) !== "N/D"
                              ? formatValue(
                                getHistoryValue(
                                  indicator,
                                  historicalPeriods[0],
                                ),
                                indicator.unit,
                              )
                              : "N/D"}
                          </td>
                          <td className="px-4 py-4 text-center text-sm bg-gray-50">
                            {getHistoryValue(
                              indicator,
                              historicalPeriods[1],
                            ) !== "N/D"
                              ? formatValue(
                                getHistoryValue(
                                  indicator,
                                  historicalPeriods[1],
                                ),
                                indicator.unit,
                              )
                              : "N/D"}
                          </td>
                          <td className="px-4 py-4 text-center text-sm bg-gray-50">
                            {getHistoryValue(
                              indicator,
                              historicalPeriods[2],
                            ) !== "N/D"
                              ? formatValue(
                                getHistoryValue(
                                  indicator,
                                  historicalPeriods[2],
                                ),
                                indicator.unit,
                              )
                              : "N/D"}
                          </td>

                          {/* Current period data */}
                          <td className="px-4 py-4 text-center text-sm font-medium bg-green-50">
                            {indicator.current
                              ? formatValue(
                                indicator.current.realized,
                                indicator.unit,
                              )
                              : "N/D"}
                          </td>
                          <td className="px-4 py-4 text-center text-sm bg-green-50">
                            {indicator.current
                              ? formatValue(
                                indicator.current.target,
                                indicator.unit,
                              )
                              : "N/D"}
                          </td>
                          <td className="px-4 py-4 text-center text-sm font-bold bg-green-50">
                            {indicator.current
                              ? `${indicator.current.percent.toFixed(1)}%`
                              : "N/D"}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {indicator.current &&
                              getStatusIcon(indicator.current.statusT)}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {indicator.current &&
                              getTrendIcon(indicator.current.trend)}
                          </td>
                        </tr>
                      )),
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {filteredData.length === 0 && (
            <div className="text-center py-16">
              <div className="p-4 bg-gray-100 rounded-full inline-block mb-4">
                <BarChart3 className="w-12 h-12 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg font-medium">
                Nenhum resultado encontrado
              </p>
              <p className="text-gray-400 mt-2">
                Tente ajustar os filtros de busca
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Exportação PDF */}
      <PDFExportModal
        isOpen={showPDFModal}
        onClose={() => setShowPDFModal(false)}
        scorecardData={filteredData}
        selectedPeriod={selectedPeriod}
        globalPerf={globalPerf}
      />
    </div>
  );
};

export default BalancedScorecard;
